import { forwardRef } from 'react';
import type { ComponentProps } from 'react';
import { CNButton } from '@zawarski/eti-ui';
import { cn } from '@utils/index';

type IconButtonProps = Omit<ComponentProps<'button'>, 'ref'> & {
  /** `small` is the 34px button of dense rows; the default is 40px. */
  size?: 'small' | 'medium';
  /** On the blue header bars: a white hover wash in place of the grey one. */
  onDark?: boolean;
};

/**
 * The round icon-only button of the legacy pages, on eti-ui's ghost CNButton. The size, corners
 * and hover that differ from its icon button are marked important so they also beat eti-ui's
 * variant classes (hover:, [&_svg]:). Icons keep their own size class (`rep:size-*`); an icon
 * without one gets 24px, not eti-ui's 16px.
 * CNButton renders over a native button (asChild) so the ref reaches the DOM, which Radix
 * triggers (MoreMenu) need to anchor their popup; see FileLibrary's TriggerButton.
 */
const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ size = 'medium', onDark, className, type, children, ...props }, ref) => (
    <CNButton
      asChild
      variant='ghost'
      size='icon'
      className={cn(
        'rep:rounded-full! rep:p-0! rep:[&_svg:not([class*=size-])]:size-6!',
        size === 'small' ? 'rep:size-8.5!' : 'rep:size-10!',
        onDark ? 'rep:hover:bg-white/20!' : 'rep:hover:bg-black/5!',
        className,
      )}>
      <button ref={ref} type={type ?? 'button'} {...props}>
        {children}
      </button>
    </CNButton>
  ),
);
IconButton.displayName = 'IconButton';

export default IconButton;
